import { type Atom, atom, type Getter } from 'jotai';
import { selectAtom } from 'jotai/utils';

import type { SummaryConfig } from '@repo/sdk/models/components';

import { allEquipmentResponseAtom } from '@/features/equipment-browser/equipment-browser-page/state/equipment-browser-response.state';
import { evaluateStates } from '@/readings-settings/conditional-state/reactive-condition-evaluator';

import type { FaultedDevice } from '../faulted-devices.types';
import {
  areFaultedDeviceListsEqual,
  buildFaultedDevice,
  hasResetFaultStates,
  isFaultedAnimaticState,
  isResetFaultStateName,
  isSameFaultedDevice,
  sortFaultedDevices,
} from '../faulted-devices.utils';

/** The device's Reset Fault action component and its active Reset Fault state, if any. */
function findActiveResetFault(get: Getter, summary: SummaryConfig) {
  for (const actionComponent of summary.actionComponents ?? []) {
    if (!hasResetFaultStates(actionComponent)) continue;

    const activeState = evaluateStates(get, actionComponent.states);
    if (activeState && isResetFaultStateName(activeState.stateName)) {
      return { actionComponent, activeState };
    }
  }
  return undefined;
}

/**
 * One device, if its animatic is faulted, evaluated against live settings/readings,
 * with how it can be reset from its Reset Fault action. Uses the same reactive
 * evaluator as `useActiveState`.
 *
 * One atom per device, so a reading re-evaluates only the device whose conditions
 * read it. A single atom over every device re-ran them all on every streamed
 * update and kept the page busy. It keeps its previous value while the device's
 * faulted state is unchanged, so live updates don't rebuild the list.
 *
 * Cached by summary in a WeakMap, so a replaced All Equipment response (another
 * facility, changed config) releases its summaries and their atoms.
 */
const faultedDeviceAtoms = new WeakMap<SummaryConfig, Atom<FaultedDevice | null>>();

function faultedDeviceAtom(summary: SummaryConfig): Atom<FaultedDevice | null> {
  let deviceAtom = faultedDeviceAtoms.get(summary);
  if (!deviceAtom) {
    deviceAtom = selectAtom(
      atom((get): FaultedDevice | null => {
        const animaticState = evaluateStates(get, summary.animatic?.primitive.states ?? []);
        if (!isFaultedAnimaticState(animaticState)) return null;

        const resetFault = findActiveResetFault(get, summary);
        return buildFaultedDevice(summary, resetFault?.actionComponent, resetFault?.activeState);
      }),
      (device) => device,
      isSameFaultedDevice
    );
    faultedDeviceAtoms.set(summary, deviceAtom);
  }
  return deviceAtom;
}

/**
 * Every faulted device in All Equipment (the default view), whatever view is
 * showing, ignoring search and filters.
 *
 * Update Cascade:
 * All Equipment view response, or a device's faulted / Reset Fault condition ref changes
 *  -> that device's active animatic state and Reset Fault state are re-evaluated
 *    -> faultedDevices (only when the set of devices or their active state changed)
 */
const allFaultedDevices = atom<FaultedDevice[]>((get) =>
  sortFaultedDevices(
    (get(allEquipmentResponseAtom)?.summaryConfigs ?? []).flatMap(
      (summary) => get(faultedDeviceAtom(summary)) ?? []
    )
  )
);

const FaultedDevicesState = {
  allFaultedDevices,
  faultedDevices: selectAtom(allFaultedDevices, (devices) => devices, areFaultedDeviceListsEqual),
};

FaultedDevicesState.allFaultedDevices.debugLabel = 'FaultedDevicesState.AllFaultedDevices';
FaultedDevicesState.faultedDevices.debugLabel = 'FaultedDevicesState.FaultedDevices';

export default FaultedDevicesState;
